import { useState, useEffect, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Clock, CheckCircle2, Upload,
  Calendar, Phone, Mail, ChevronDown, ChevronRight,
  Loader2, X, Building2, Layers, RefreshCw, Edit3, Save, BadgeCheck, ExternalLink
} from 'lucide-react';
import BulkWarrantyUpload from './BulkWarrantyUpload';

const statusColors = {
  active: { bg: 'bg-emerald-100 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-300 dark:border-emerald-900', label: 'Active' },
  expiring_soon: { bg: 'bg-amber-100 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-300 dark:border-amber-900', label: 'Expiring Soon' },
  expired: { bg: 'bg-red-100 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-300 dark:border-red-900', label: 'Expired' },
  pending: { bg: 'bg-muted/50', text: 'text-muted-foreground', border: 'border-border', label: 'Pending' },
};

function WarrantyCard({ warranty, projectId, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const st = statusColors[warranty.status] || statusColors.pending;

  const startEdit = (e) => {
    e.stopPropagation();
    setForm({
      warranty_start: warranty.warranty_start || '',
      warranty_end: warranty.warranty_end || '',
      coverage_details: warranty.coverage_details || '',
      vendor_name: warranty.vendor_name || '',
      registration_number: warranty.registration_number || '',
      contact_name: warranty.contact_name || '',
      contact_phone: warranty.contact_phone || '',
      contact_email: warranty.contact_email || '',
    });
    setEditing(true);
    setExpanded(true);
  };

  const field = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/closeout/projects/${projectId}/warranties/${warranty.id}`, form);
      setEditing(false);
      onUpdated && onUpdated();
      toast.success('Warranty updated');
    } catch (err) { console.error(err); toast.error(err?.response?.data?.detail || 'Could not save warranty'); }
    finally { setSaving(false); }
  };

  const verify = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await api.put(`/closeout/projects/${projectId}/warranties/${warranty.id}`, { verified: true });
      onUpdated && onUpdated();
      toast.success('Warranty verified');
    } catch (err) { console.error(err); toast.error(err?.response?.data?.detail || 'Could not verify warranty'); }
    finally { setSaving(false); }
  };

  const inputCls = "mt-0.5 w-full bg-card border border-border rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary/50";

  return (
    <div className={`border rounded-sm transition-colors ${st.border} ${st.bg}`} data-testid={`warranty-${warranty.id}`}>
      <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <Shield className={`w-5 h-5 ${st.text} shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground truncate">{warranty.equipment_name}</p>
            {warranty.verified && (
              <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800 rounded px-1 py-0.5 shrink-0" data-testid={`warranty-verified-${warranty.id}`}>
                <BadgeCheck className="w-3 h-3" /> Verified
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
            {warranty.vendor_name && <span>{warranty.vendor_name}</span>}
            {warranty.warranty_type && <span className="font-mono">{warranty.warranty_type}</span>}
            {warranty.division_trade_name && (
              <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{warranty.division_trade_name}</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${st.bg} ${st.text} border ${st.border}`}>
            {st.label}
          </span>
          {warranty.days_remaining !== null && warranty.days_remaining !== undefined && (
            <p className={`text-xs font-mono mt-1 ${warranty.days_remaining < 0 ? 'text-red-600 dark:text-red-400' : warranty.days_remaining <= 90 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {warranty.days_remaining < 0 ? `${Math.abs(warranty.days_remaining)}d overdue` : `${warranty.days_remaining}d left`}
            </p>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-3" onClick={(e) => e.stopPropagation()}>
          {editing ? (
            <div className="space-y-2" data-testid={`warranty-edit-form-${warranty.id}`}>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] text-muted-foreground">Start
                  <input type="date" value={form.warranty_start} onChange={(e) => field('warranty_start', e.target.value)} data-testid={`warranty-edit-start-${warranty.id}`} className={inputCls} />
                </label>
                <label className="text-[10px] text-muted-foreground">End
                  <input type="date" value={form.warranty_end} onChange={(e) => field('warranty_end', e.target.value)} data-testid={`warranty-edit-end-${warranty.id}`} className={inputCls} />
                </label>
                <label className="text-[10px] text-muted-foreground">Vendor
                  <input value={form.vendor_name} onChange={(e) => field('vendor_name', e.target.value)} data-testid={`warranty-edit-vendor-${warranty.id}`} className={inputCls} />
                </label>
                <label className="text-[10px] text-muted-foreground">Reg #
                  <input value={form.registration_number} onChange={(e) => field('registration_number', e.target.value)} data-testid={`warranty-edit-reg-${warranty.id}`} className={inputCls} />
                </label>
              </div>
              <label className="text-[10px] text-muted-foreground block">Coverage
                <textarea value={form.coverage_details} onChange={(e) => field('coverage_details', e.target.value)} rows={2} data-testid={`warranty-edit-coverage-${warranty.id}`} className={inputCls} />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="Contact" value={form.contact_name} onChange={(e) => field('contact_name', e.target.value)} data-testid={`warranty-edit-contact-${warranty.id}`} className="bg-card border border-border rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary/50" />
                <input placeholder="Phone" value={form.contact_phone} onChange={(e) => field('contact_phone', e.target.value)} data-testid={`warranty-edit-phone-${warranty.id}`} className="bg-card border border-border rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary/50" />
                <input placeholder="Email" value={form.contact_email} onChange={(e) => field('contact_email', e.target.value)} data-testid={`warranty-edit-email-${warranty.id}`} className="bg-card border border-border rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary/50" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={save} disabled={saving} data-testid={`warranty-save-${warranty.id}`} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors disabled:opacity-50">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                </button>
                <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {warranty.warranty_start && (<div><span className="text-muted-foreground">Start:</span><span className="ml-2 font-mono text-foreground/80">{warranty.warranty_start}</span></div>)}
                {warranty.warranty_end && (<div><span className="text-muted-foreground">End:</span><span className="ml-2 font-mono text-foreground/80">{warranty.warranty_end}</span></div>)}
                {warranty.registration_number && (<div><span className="text-muted-foreground">Reg #:</span><span className="ml-2 font-mono text-foreground/80">{warranty.registration_number}</span></div>)}
              </div>
              {warranty.coverage_details && (<div className="text-xs"><span className="text-muted-foreground">Coverage:</span><p className="text-muted-foreground mt-0.5">{warranty.coverage_details}</p></div>)}
              {/* Structured coverage carried from the equipment AI lookup (matches the asset inventory card) */}
              {warranty.warranty_components?.length > 0 && (
                <div className="space-y-1" data-testid={`warranty-components-${warranty.id}`}>
                  {warranty.source_type && (
                    <span className={`inline-block text-[9px] font-mono px-1.5 py-0.5 rounded border mb-1 ${
                      warranty.source_type === 'OEM'
                        ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-500'
                        : 'bg-orange-100 dark:bg-orange-950/40 border-primary/30 text-primary'
                    }`}>{warranty.source_type}</span>
                  )}
                  {warranty.warranty_components.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs px-2 py-1.5 bg-secondary/30 rounded-sm border border-border/50">
                      <span className="text-foreground/80 font-medium flex-1">{c.component}</span>
                      <span className="font-mono text-emerald-600 dark:text-emerald-400">{c.parts_years}yr parts</span>
                      <span className="font-mono text-blue-600 dark:text-blue-400">{c.labor_years}yr labor</span>
                      {c.requires_registration && <span className="text-[9px] text-amber-500">REG</span>}
                    </div>
                  ))}
                </div>
              )}
              {warranty.warranty_tips?.length > 0 && (
                <div className="text-[10px] text-muted-foreground space-y-0.5" data-testid={`warranty-tips-${warranty.id}`}>
                  {warranty.warranty_tips.map((t, i) => <p key={i}>* {t}</p>)}
                </div>
              )}
              {(warranty.product_page_url || warranty.registration_url || warranty.spec_sheet_url || warranty.lookup_url) && (
                <div className="flex gap-3 flex-wrap">
                  {warranty.product_page_url && (
                    <a href={warranty.product_page_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-emerald-500 hover:text-emerald-400"><ExternalLink className="w-3 h-3" /> Product Page</a>
                  )}
                  {warranty.registration_url && (
                    <a href={warranty.registration_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-400"><ExternalLink className="w-3 h-3" /> Register Warranty</a>
                  )}
                  {warranty.spec_sheet_url && (
                    <a href={warranty.spec_sheet_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-cyan-500 hover:text-cyan-400"><ExternalLink className="w-3 h-3" /> Spec Sheet</a>
                  )}
                  {warranty.lookup_url && !warranty.registration_url && (
                    <a href={warranty.lookup_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-primary hover:text-primary"><ExternalLink className="w-3 h-3" /> Warranty Lookup</a>
                  )}
                </div>
              )}
              <div className="flex gap-4 text-xs text-muted-foreground">
                {warranty.contact_name && <span className="flex items-center gap-1">{warranty.contact_name}</span>}
                {warranty.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{warranty.contact_phone}</span>}
                {warranty.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{warranty.contact_email}</span>}
              </div>
              {warranty.ai_extracted && (
                <p className="text-[10px] text-muted-foreground">AI extracted{warranty.ai_confidence ? ` (confidence: ${(warranty.ai_confidence * 100).toFixed(0)}%)` : ''} — review the dates and terms, then Verify.</p>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={startEdit} data-testid={`warranty-edit-${warranty.id}`} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider border border-border text-foreground/80 rounded-sm hover:bg-secondary transition-colors">
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
                {!warranty.verified && (
                  <button onClick={verify} disabled={saving} data-testid={`warranty-verify-${warranty.id}`} className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider border border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 rounded-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors disabled:opacity-50">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />} Verify
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function WarrantyPanel({ projectId }) {
  const [warranties, setWarranties] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [verifyFilter, setVerifyFilter] = useState('all');
  const [showBulk, setShowBulk] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncedRef = useRef(false);

  const fetchData = async () => {
    try {
      const [wRes, aRes] = await Promise.all([
        api.get(`/closeout/projects/${projectId}/warranties`),
        api.get(`/closeout/projects/${projectId}/warranty-alerts`),
      ]);
      // If empty, try to auto-populate from equipment-level AI warranty findings (once)
      if ((wRes.data || []).length === 0 && !syncedRef.current) {
        syncedRef.current = true;
        try {
          const sync = await api.post(`/closeout/projects/${projectId}/warranties/sync-from-assets`);
          if (sync.data?.created > 0 || sync.data?.total > 0) {
            const [w2, a2] = await Promise.all([
              api.get(`/closeout/projects/${projectId}/warranties`),
              api.get(`/closeout/projects/${projectId}/warranty-alerts`),
            ]);
            setWarranties(w2.data);
            setAlerts(a2.data);
            return;
          }
        } catch { /* non-critical */ }
      }
      setWarranties(wRes.data);
      setAlerts(aRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/closeout/projects/${projectId}/upload-warranty`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const verifiedCount = warranties.filter(w => w.verified).length;
  const unverifiedCount = warranties.length - verifiedCount;
  const filtered = warranties.filter(w => {
    const statusOk = filter === 'all' || w.status === filter;
    const verifyOk = verifyFilter === 'all'
      || (verifyFilter === 'verified' ? !!w.verified : !w.verified);
    return statusOk && verifyOk;
  });

  return (
    <div data-testid="warranty-panel">
      {/* Alert Summary */}
      {alerts && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Active', count: alerts.active_count, color: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
            { label: 'Expiring', count: alerts.expiring_soon_count, color: 'text-amber-700 dark:text-amber-400', icon: Clock },
            { label: 'Expired', count: alerts.expired_count, color: 'text-red-700 dark:text-red-400', icon: AlertTriangle },
            { label: 'Total', count: alerts.total, color: 'text-foreground/80', icon: Shield },
          ].map(a => (
            <div key={a.label} className="bg-card border border-border rounded-sm p-3 text-center">
              <a.icon className={`w-4 h-4 mx-auto mb-1 ${a.color}`} />
              <p className={`font-mono text-lg font-bold ${a.color}`}>{a.count}</p>
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{a.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 flex-wrap">
          {['all', 'active', 'expiring_soon', 'expired', 'pending'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`warranty-status-filter-${f}`}
              className={`px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
                filter === f ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
              }`}>
              {f === 'all' ? 'All' : f.replace('_', ' ')}
            </button>
          ))}
          <span className="w-px h-4 bg-border mx-1.5" />
          {[['all', 'Any'], ['verified', `Verified (${verifiedCount})`], ['unverified', `Unverified (${unverifiedCount})`]].map(([v, label]) => (
            <button key={v} onClick={() => setVerifyFilter(v)}
              data-testid={`warranty-verify-filter-${v}`}
              className={`px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
                verifyFilter === v
                  ? 'bg-emerald-600/15 text-emerald-700 dark:text-emerald-400 border border-emerald-400/40'
                  : 'text-muted-foreground hover:text-foreground/80'
              }`}>
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              setSyncing(true);
              try {
                await api.post(`/closeout/projects/${projectId}/warranties/sync-from-assets`);
                await fetchData();
              } catch (err) { console.error(err); }
              finally { setSyncing(false); }
            }}
            data-testid="sync-warranties-btn"
            title="Create/refresh warranty records from equipment AI findings"
            className="flex items-center gap-2 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm border border-emerald-300 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Sync from Equipment
          </button>
          <button
            onClick={() => setShowBulk(!showBulk)}
            data-testid="bulk-upload-toggle"
            className={`flex items-center gap-2 px-3 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors ${
              showBulk
                ? 'bg-violet-600/20 text-violet-700 dark:text-violet-400 border border-violet-300 dark:border-violet-800/40'
                : 'text-violet-700 dark:text-violet-400 border border-violet-300 dark:border-violet-800/40 hover:bg-violet-50 dark:hover:bg-violet-950/30'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            Bulk Upload
          </button>
          <label className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-heading uppercase tracking-wider rounded-sm cursor-pointer transition-colors" data-testid="upload-warranty-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Single Upload
            <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUpload} />
          </label>
        </div>
      </div>

      {/* Bulk Upload Panel */}
      {showBulk && (
        <div className="mb-4 p-4 bg-card border border-violet-800/30 rounded-sm">
          <BulkWarrantyUpload
            projectId={projectId}
            onComplete={() => {
              setShowBulk(false);
              fetchData();
            }}
          />
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-card border border-dashed border-border rounded-sm">
          <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {filter === 'all' ? 'No warranties recorded yet' : `No ${filter.replace('_', ' ')} warranties`}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Upload warranty certificates for AI extraction</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(w => <WarrantyCard key={w.id} warranty={w} projectId={projectId} onUpdated={fetchData} />)}
        </div>
      )}
    </div>
  );
}
